/**
 * promotion-ladder.js -- the Environment Ladder hero component: the
 * selected family's DEV/TEST/PROD stations rendered as the promotion
 * interface itself (design: docs/superpowers/specs/2026-07-11-v2-promotion-
 * ladder-design.md, "The Environment Ladder (hero)").
 *
 * createLadder(mount, opts) -> { setState, onSegmentClick, onStationPick, destroy }
 * PURE render from state -- no fetching, no model knowledge. The wizard
 * (promotion-wizard.js, a later task) owns orchestration: it asks
 * promotion-model.js for data and calls setState() with the result.
 *
 * state = {
 *   rungs: { dev: [conn], test: [conn], prod: [conn], unassigned: [conn] },
 *   armed: { fromEnv, toEnv } | null,
 *   picks: { dev?: id, test?: id, prod?: id },   // multi-connection rung's current pick
 *   release: { id, short, assetCount, age, sourceEnv, testClean } | null,
 *   gates:  { prodLocked: bool, prodReason: string },
 *   busy:   "snapshot" | "apply" | null,
 * }
 * `conn` is anything with at least `{id, label}` (a hosted connection or a
 * local pipeline stage, per promotion-model.js).
 *
 * IMPORTANT for the caller (promotion-wizard.js): `state.rungs` is ONE
 * family's rungs, flattened from promotion-model.js's `buildRungs()`
 * per-family entry
 * ({family, rungs:{dev,test,prod}, unassigned}) by dropping the `family`
 * wrapper key and folding `unassigned` in alongside dev/test/prod:
 *   ladder.setState({ rungs: { ...fam.rungs, unassigned: fam.unassigned }, ... })
 *
 * opts = { families?: [{family}], onFamilySwitch?(family) }
 * The family switcher is opts-driven chrome, independent of `state` (whose
 * `rungs` is already scoped to one family) -- rendered only when
 * opts.families has more than one entry; invisible chrome otherwise.
 *
 * The first-run onboarding line renders a `data-action="open-settings"`
 * button rather than wiring a click handler here: this module has no model/
 * settings-dialog knowledge (pure render), so promotion-wizard.js attaches
 * the click -> openSettingsDialog() wiring where it already knows how the
 * app opens Settings.
 *
 * ladderViewModel(state) is the pure state -> render-model seam
 * (promotion-ladder.test.mjs exercises this directly, no DOM required).
 */
import { esc, escAttr } from "./state.js";
import { iconHtml } from "./icons.js";

const ENVS = ["dev", "test", "prod"];
const ENV_LABEL = { dev: "DEV", test: "TEST", prod: "PROD" };
// Ascending pairs only -- the ladder never offers a downward or lateral
// route. dev-prod is the "skip" segment (both adjacent hops populated at
// once is the common case -- see the design doc's ASCII mock).
const SEG_PAIRS = [["dev", "test"], ["dev", "prod"], ["test", "prod"]];

// ---------------------------------------------------------------------------
// Pure view-model (unit-tested seam)
// ---------------------------------------------------------------------------

function _rung(rungs, env) {
  return rungs && Array.isArray(rungs[env]) ? rungs[env] : [];
}

/** @param {number} n @returns {string} "0 assets" / "1 asset" / "44 assets" */
export function assetCountText(n) {
  const count = Number(n) || 0;
  return `${count} asset${count === 1 ? "" : "s"}`;
}

function _busyLabel(busy, armed) {
  if (!busy || !armed) return "";
  const from = ENV_LABEL[armed.fromEnv] || armed.fromEnv;
  const to = ENV_LABEL[armed.toEnv] || armed.toEnv;
  if (busy === "snapshot") return `Snapshotting ${from}…`;
  if (busy === "apply") return `Applying ${from} → ${to}…`;
  return "";
}

/**
 * state -> render model. Pure; no DOM, no escaping (the DOM render below
 * escapes everything it interpolates).
 */
export function ladderViewModel(state) {
  const s = state || {};
  const rungs = s.rungs || {};
  const picks = s.picks || {};
  const armed = s.armed || null;
  const gates = s.gates || {};
  const release = s.release || null;
  const busy = s.busy || null;

  const firstRun = ENVS.every((env) => _rung(rungs, env).length === 0);

  const stations = ENVS.map((env) => {
    const connections = _rung(rungs, env);
    const empty = connections.length === 0;
    const hasPicker = connections.length > 1;
    const pickId = picks[env] != null ? picks[env] : connections[0] ? connections[0].id : null;
    const picked = connections.find((c) => c.id === pickId) || connections[0] || null;
    const locked = env === "prod" && !!gates.prodLocked;
    const lockReason = locked ? gates.prodReason || "" : "";
    // Lit only once the gate is actually open AND the release earned it --
    // "the ladder gate ... visibly unlocks" once a clean TEST apply exists.
    const unlocked = env === "prod" && !gates.prodLocked && !!(release && release.testClean);
    const pulsing = busy === "snapshot" && !!armed && armed.fromEnv === env;
    const armedRole = armed ? (armed.fromEnv === env ? "from" : armed.toEnv === env ? "to" : null) : null;
    const releaseHere = release && release.sourceEnv === env ? release : null;
    return {
      env, label: ENV_LABEL[env], connections, empty, hasPicker,
      pickedId: picked ? picked.id : null, picked,
      locked, lockReason, unlocked, pulsing, armedRole, release: releaseHere,
    };
  });

  const segments = SEG_PAIRS.map(([from, to]) => {
    const legal = _rung(rungs, from).length > 0 && _rung(rungs, to).length > 0;
    const isArmed = !!armed && armed.fromEnv === from && armed.toEnv === to;
    const dashFlow = isArmed && busy === "apply";
    return { from, to, legal, interactive: legal, armed: isArmed, dashFlow };
  });

  return {
    firstRun,
    stations,
    segments,
    unassigned: _rung(rungs, "unassigned"),
    busy,
    busyLabel: _busyLabel(busy, armed),
  };
}

// ---------------------------------------------------------------------------
// DOM render (consumes the view model; every interpolation is escaped here)
// ---------------------------------------------------------------------------

function _envBadge(env) {
  return `<span class="env2 ${env}">${ENV_LABEL[env]}</span>`;
}

function _shortArrowSegHtml(seg) {
  const pairCls = `pl-pair-${seg.from}-${seg.to}`;
  if (!seg.legal) {
    return `<span class="pl-seg off ${pairCls}" aria-hidden="true">${iconHtml("arrow-right", { size: "sm" })}</span>`;
  }
  const cls = ["pl-seg", pairCls];
  if (seg.armed) cls.push("armed");
  if (seg.dashFlow) cls.push("dash-flow");
  const label = `Promote ${ENV_LABEL[seg.from]} to ${ENV_LABEL[seg.to]}`;
  return `<button type="button" class="${cls.join(" ")}" data-from="${escAttr(seg.from)}" data-to="${escAttr(seg.to)}" aria-label="${escAttr(label)}"${seg.armed ? ' aria-pressed="true"' : ""}>${iconHtml("arrow-right", { size: "sm" })}</button>`;
}

function _skipSegHtml(seg) {
  // A thin separator (not another arrow glyph) -- the icon below already
  // IS the arrow; repeating it in the text read as a confusing double arrow.
  const label = `${ENV_LABEL[seg.from]} › ${ENV_LABEL[seg.to]}`;
  if (!seg.legal) {
    return `<span class="pl-seg pl-skip off" aria-hidden="true">${iconHtml("arrow-right", { size: "sm" })}<span class="pl-skip-label">${esc(label)}</span></span>`;
  }
  const cls = ["pl-seg", "pl-skip"];
  if (seg.armed) cls.push("armed");
  if (seg.dashFlow) cls.push("dash-flow");
  const aria = `Promote ${ENV_LABEL[seg.from]} directly to ${ENV_LABEL[seg.to]}`;
  return `<button type="button" class="${cls.join(" ")}" data-from="${escAttr(seg.from)}" data-to="${escAttr(seg.to)}" aria-label="${escAttr(aria)}"${seg.armed ? ' aria-pressed="true"' : ""}>${iconHtml("arrow-right", { size: "sm" })}<span class="pl-skip-label">${esc(label)}</span></button>`;
}

function _pickerHtml(st) {
  const options = st.connections
    .map((c) => `<option value="${escAttr(c.id)}"${c.id === st.pickedId ? " selected" : ""}>${esc(c.label || c.id)}</option>`)
    .join("");
  return `<div class="pl-pick">
    <select class="pl-pick-select" data-env="${escAttr(st.env)}" aria-label="Choose the ${escAttr(st.label)} connection">${options}</select>
    <div class="pl-pick-face" aria-hidden="true">
      <span class="pl-pick-name">${esc(st.picked ? st.picked.label || st.picked.id : "")}</span>
      ${iconHtml("chevron-down", { size: "sm" })}
    </div>
  </div>`;
}

function _releaseChipHtml(rel) {
  const clean = rel.testClean
    ? `<span class="pl-rel-clean">${iconHtml("check", { size: "sm" })}TEST</span>`
    : "";
  return `<div class="pl-release">
    <span class="pl-rel-id">${esc(rel.short || rel.id || "")}</span>
    <span class="pl-rel-dot">·</span>
    <span class="pl-rel-count">${esc(assetCountText(rel.assetCount))}</span>
    <span class="pl-rel-dot">·</span>
    <span class="pl-rel-age">${esc(rel.age || "")}</span>
    ${clean}
  </div>`;
}

// role="group" + aria-label so a screen-reader user browsing the ladder gets each
// station's identity announced as one unit (env + picked connection + lock state) --
// the station itself has no click action of its own (the picker <select>/segment
// <button>s inside already carry their own aria-labels), so this is a landmark, not an
// added tab stop.
function _stationAriaLabel(st) {
  const who = !st.empty && st.picked ? (st.picked.label || st.picked.id) : "no connection assigned";
  const lockSuffix = st.locked ? ", locked" : st.unlocked ? ", unlocked" : "";
  return `${st.label} station: ${who}${lockSuffix}`;
}

function _stationHtml(st) {
  const cls = ["pl-station", `pl-env-${st.env}`];
  if (st.empty) cls.push("empty");
  if (st.locked) cls.push("locked");
  if (st.pulsing) cls.push("pulsing");
  const armedAttr = st.armedRole ? ` data-armed-role="${escAttr(st.armedRole)}"` : "";

  const lockChip = st.locked
    ? `<span class="pl-lock" title="${escAttr(st.lockReason)}">${iconHtml("lock", { size: "sm" })}</span>`
    : st.unlocked
      ? `<span class="pl-unlocked" title="Unlocked by a clean TEST apply">${iconHtml("unlock", { size: "sm" })}</span>`
      : "";

  let body;
  if (st.empty) {
    body = `<div class="pl-station-empty">No connection</div>`;
  } else if (st.hasPicker) {
    body = _pickerHtml(st);
  } else {
    body = `<div class="pl-station-id">${esc(st.picked ? st.picked.label || st.picked.id : "")}</div>`;
  }

  const reason = st.locked ? `<div class="pl-lock-reason">${esc(st.lockReason)}</div>` : "";
  const relChip = st.release ? _releaseChipHtml(st.release) : "";

  return `<div class="${cls.join(" ")}" data-env="${escAttr(st.env)}" role="group" aria-label="${escAttr(_stationAriaLabel(st))}"${armedAttr}>
    <div class="pl-station-head">${_envBadge(st.env)}${lockChip}</div>
    <div class="pl-station-body">${body}</div>
    ${reason}
    ${relChip}
  </div>`;
}

function _trayChipHtml(c) {
  return `<span class="chip pl-tray-chip">${esc(c.label || c.id)}</span>`;
}

function _renderBody(vm) {
  const s = (i) => vm.segments[i];
  const grid = `
    <div class="pl-grid${vm.firstRun ? " pl-skeleton" : ""}">
      <div class="pl-skip-row">${_skipSegHtml(s(1))}</div>
      <div class="pl-row">
        ${_stationHtml(vm.stations[0])}
        ${_shortArrowSegHtml(s(0))}
        ${_stationHtml(vm.stations[1])}
        ${_shortArrowSegHtml(s(2))}
        ${_stationHtml(vm.stations[2])}
      </div>
    </div>`;

  const busyLine = vm.busy
    ? `<div class="pl-busy-line" role="status" aria-live="polite">${iconHtml("activity", { size: "sm" })}<span>${esc(vm.busyLabel)}</span></div>`
    : `<div class="pl-busy-line" role="status" aria-live="polite" hidden></div>`;

  const onboarding = vm.firstRun
    ? `<div class="pl-onboarding">
         <p>Assign environments to your connections to start promoting.</p>
         <button type="button" class="stbtn" data-action="open-settings">${iconHtml("settings", { size: "sm" })}Open connection settings</button>
       </div>`
    : "";

  const tray = vm.unassigned && vm.unassigned.length
    ? `<div class="pl-tray">
         <div class="pl-tray-label">Unassigned</div>
         <div class="pl-tray-list">${vm.unassigned.map(_trayChipHtml).join("")}</div>
       </div>`
    : "";

  return `${busyLine}${grid}${onboarding}${tray}`;
}

// ---------------------------------------------------------------------------
// DOM factory
// ---------------------------------------------------------------------------

export function createLadder(mount, opts = {}) {
  const families = Array.isArray(opts.families) ? opts.families : [];
  let activeFamily = families.length ? families[0].family : null;
  let segClickCb = null;
  let stationPickCb = null;

  mount.innerHTML = `
    <div class="pladder">
      <div class="pl-switcher-row" data-pl-switcher></div>
      <div class="pl-body" data-pl-body></div>
    </div>`;

  const switcherEl = mount.querySelector("[data-pl-switcher]");
  const bodyEl = mount.querySelector("[data-pl-body]");

  function _renderSwitcher() {
    if (families.length <= 1) {
      switcherEl.innerHTML = "";
      return;
    }
    switcherEl.innerHTML = `<div class="seg2 pl-seg2">${families
      .map((f) => `<button type="button" class="${f.family === activeFamily ? "on" : ""}" data-pl-family="${escAttr(f.family)}">${esc(f.family)}</button>`)
      .join("")}</div>`;
  }
  _renderSwitcher();

  function _onSwitcherClick(e) {
    const btn = e.target.closest("button[data-pl-family]");
    if (!btn) return;
    const family = btn.getAttribute("data-pl-family");
    if (family === activeFamily) return;
    activeFamily = family;
    _renderSwitcher();
    if (typeof opts.onFamilySwitch === "function") opts.onFamilySwitch(family);
  }
  switcherEl.addEventListener("click", _onSwitcherClick);

  function setState(state) {
    bodyEl.innerHTML = _renderBody(ladderViewModel(state));
  }

  function onSegmentClick(fn) {
    segClickCb = fn;
  }
  function onStationPick(fn) {
    stationPickCb = fn;
  }

  function _onBodyClick(e) {
    const segBtn = e.target.closest("button.pl-seg[data-from][data-to]");
    if (!segBtn) return;
    if (typeof segClickCb === "function") {
      segClickCb(segBtn.getAttribute("data-from"), segBtn.getAttribute("data-to"));
    }
    // Note: the onboarding `[data-action="open-settings"]` button is
    // intentionally left unwired here -- see the module doc comment above.
  }

  function _onBodyChange(e) {
    const sel = e.target.closest("select.pl-pick-select");
    if (!sel) return;
    const env = sel.getAttribute("data-env");
    if (env && typeof stationPickCb === "function") stationPickCb(env, sel.value);
  }

  bodyEl.addEventListener("click", _onBodyClick);
  bodyEl.addEventListener("change", _onBodyChange);

  function destroy() {
    switcherEl.removeEventListener("click", _onSwitcherClick);
    bodyEl.removeEventListener("click", _onBodyClick);
    bodyEl.removeEventListener("change", _onBodyChange);
    mount.innerHTML = "";
  }

  return { setState, onSegmentClick, onStationPick, destroy };
}
